const MAX_BASE64_SIZE = 900 * 1024;
const DEFAULT_MAX_DIMENSION = 1200;
const DEFAULT_QUALITY = 0.8;
const MIN_QUALITY = 0.4;
const MIN_DIMENSION = 400;

const readFileAsDataURL = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('Failed to read image file.'));
    reader.readAsDataURL(file);
  });

const loadImage = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Failed to load image for compression.'));
    img.src = src;
  });

const drawToCanvas = (img, maxDimension) => {
  let { width, height } = img;
  if (width > height && width > maxDimension) {
    height = Math.round((height * maxDimension) / width);
    width = maxDimension;
  } else if (height > maxDimension) {
    width = Math.round((width * maxDimension) / height);
    height = maxDimension;
  }
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(img, 0, 0, width, height);
  return canvas;
};

export const compressImageFile = async (file) => {
  if (!file) {
    throw new Error('No image file provided.');
  }

  const originalDataUrl = await readFileAsDataURL(file);

  if (originalDataUrl.length <= MAX_BASE64_SIZE) {
    return originalDataUrl;
  }

  const img = await loadImage(originalDataUrl);

  let quality = DEFAULT_QUALITY;
  let dimension = DEFAULT_MAX_DIMENSION;
  let result = originalDataUrl;

  for (let attempt = 0; attempt < 8; attempt += 1) {
    const canvas = drawToCanvas(img, dimension);
    result = canvas.toDataURL('image/jpeg', quality);

    if (result.length <= MAX_BASE64_SIZE) {
      return result;
    }

    if (quality > MIN_QUALITY) {
      quality = Math.max(MIN_QUALITY, quality - 0.1);
    } else if (dimension > MIN_DIMENSION) {
      dimension = Math.max(MIN_DIMENSION, Math.round(dimension * 0.8));
    } else {
      break;
    }
  }

  if (result.length > MAX_BASE64_SIZE) {
    throw new Error(
      'Image is too large to save even after compression. Please choose a smaller image.'
    );
  }

  return result;
};

export const isDataUrl = (value) =>
  typeof value === 'string' && value.startsWith('data:image');
